import './collageSlider.scss';

import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { CollageQuote } from '../collageQuote';
import { HostedVideo } from '../hosted-video';

export function CollageSlider({
	backgroundColor,
	cardStyle,
	contentsHeight,
	sliderItems,
	textColor,
	verticalPadding,
}) {
	// Hooks
	const isMobile = useMediaQuery('(max-width: 799px)');
	const isTablet = useMediaQuery(
		'(min-width: 800px) and (max-width: 1279px)'
	);
	const isDesktop = useMediaQuery('(min-width: 1280px)');

	//Draggable on desktop
	const sliderRef = useRef(null);

	useEffect(() => {
		const slider = sliderRef.current;
		if (!slider || isMobile) return;

		const scrollToCenter = () => {
			const maxScroll = slider.scrollWidth - slider.clientWidth;
			slider.scrollLeft = maxScroll / 2;
		};
		const timeout = setTimeout(scrollToCenter, 100);

		let isDown = false;
		let startX = 0;
		let scrollLeft = 0;

		const handleMouseDown = (e) => {
			isDown = true;
			slider.classList.add('dragging');
			startX = e.pageX;
			scrollLeft = slider.scrollLeft;
		};

		const handleMouseMove = (e) => {
			if (!isDown) return;
			e.preventDefault();
			const x = e.pageX;
			const walk = x - startX;
			slider.scrollLeft = scrollLeft - walk;
		};

		const stopDragging = () => {
			isDown = false;
			slider.classList.remove('dragging');
		};

		slider.addEventListener('mousedown', handleMouseDown);
		slider.addEventListener('mousemove', handleMouseMove);
		slider.addEventListener('mouseup', stopDragging);
		slider.addEventListener('mouseleave', stopDragging);

		return () => {
			clearTimeout(timeout);
			slider.removeEventListener('mousedown', handleMouseDown);
			slider.removeEventListener('mousemove', handleMouseMove);
			slider.removeEventListener('mouseup', stopDragging);
			slider.removeEventListener('mouseleave', stopDragging);
		};
	}, [isMobile]);

	const modulePaddingTop = verticalPadding?.modulePaddingTop;
	const modulePaddingBottom = verticalPadding?.modulePaddingBottom;

	const videoHeight = {
		height: isMobile
			? `${contentsHeight?.heightMobile}px`
			: isTablet
				? `${contentsHeight?.heightTablet}px`
				: isDesktop
					? `${contentsHeight?.heightDesktop}px`
					: 'auto',
	};

	return (
		<div
			ref={sliderRef}
			className={classNames(
				backgroundColor && `b${backgroundColor}`,
				textColor,
				'collage-slider',
				modulePaddingBottom,
				modulePaddingTop,
				backgroundColor && `b${backgroundColor}`
			)}
		>
			<div className='collage-slider-inner'>
				{sliderItems?.map((item) => {
					const { _key, _type } = item;

					switch (_type) {
						case 'videoHosted':
							return (
								<HostedVideo
									key={_key}
									videoPlayOptions={item.videoPlayOptions}
									video={item.video}
									quoteOverlay={item.quoteOverlay}
									thumbnail={item.thumbnail}
									videoHeightStyle={videoHeight}
								/>
							);
						case 'collageQuote':
							return (
								<CollageQuote
									key={_key}
									cardHeight={videoHeight}
									cardStyle={cardStyle}
									aspectRatio={item.aspectRatio}
									quote={item.richQuote}
									source={item.subheader}
									backgroundColor={`b${item.backgroundColor}`}
									textColor={`${item.textColor}`}
									backgroundImage={item.backgroundImage}
								/>
							);
						default:
							return null; // Return null if no valid videoPlayOptions match
					}
				})}
			</div>
		</div>
	);
}
